import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Download, FileText, PencilLine } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip } from "@/components/ui/Tooltip";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { SectionCard, TableShell, Th } from "../ui";
import { useGenerateTomTitles, useGeneratedHistory, useTaskPolling, useTomTitles } from "../../hooks";
import { TOM_TITLES_CONTEXT } from "../../api";
import { apiErr, fmtDateTime } from "../../utils";
import type { GeneratedDoc, PowerProject, TomCompletenessReport } from "../../types";

// TOM II „Tytuły prawne do nieruchomości" — jedno kliknięcie składa cały tom:
// część redagowaną z szablonu (DOCX do poprawek projektanta) i scalony PDF całości,
// w którym za przekładkami siedzą skany wypisów i tytułów prawnych.
//
// Karta pokazuje RAPORT KOMPLETNOŚCI zanim ktokolwiek kliknie: braki widać przed
// złożeniem tomu, a nie po. Braki NIE blokują generowania (tom powstaje z dziurami
// do uzupełnienia ręcznego) — blokuje wyłącznie brak nazwy zamierzenia i brak szablonu.

const GAPS: { key: keyof TomCompletenessReport; label: string; hint: string }[] = [
  {
    key: "no_owners",
    label: "Bez wpisanych właścicieli",
    hint: "Wykaz z pkt 2 dostanie pusty wiersz — uzupełnij właścicieli (wypis albo wpis ręczny).",
  },
  {
    key: "no_kw_analysis",
    label: "Bez analizy księgi wieczystej",
    hint: "Protokół z pkt 3 zostawi pustą komórkę „Wynik analizy” do wypełnienia ręcznego.",
  },
  {
    key: "no_wypis",
    label: "Bez wypisu z przypisanymi stronami",
    hint: "Do pkt 4 nie wejdzie skan tej działki — wgraj wypis i zmapuj strony (zakładka Wypisy).",
  },
  {
    key: "no_title",
    label: "Bez dokumentu tytułu prawnego",
    hint: "Do pkt 5 nie wejdzie ani zgoda właściciela, ani decyzja dotycząca tej działki.",
  },
];

// Dymek ma mówić KRÓTKO czego brakuje — kilka pierwszych pozycji i licznik reszty.
// Pełna lista zostaje pod kliknięciem (i w `report`), bo przy kilkudziesięciu działkach
// dymek przestałby być do czytania.
const TOOLTIP_ROWS = 6;
const TOOLTIP_ROW_CHARS = 70;

export function shortList(rows: string[]): string {
  const shown = rows
    .slice(0, TOOLTIP_ROWS)
    .map((r) => (r.length > TOOLTIP_ROW_CHARS ? `${r.slice(0, TOOLTIP_ROW_CHARS)}…` : r))
    .join(", ");
  const rest = rows.length - TOOLTIP_ROWS;
  return rest > 0 ? `${shown} … i ${rest} więcej` : shown;
}

/** Rozpiska braków po kategoriach — dymek przy podsumowaniu w historii. */
export function gapBreakdown(report: Partial<TomCompletenessReport>): string {
  return GAPS.map((g) => {
    const rows = (report[g.key] as string[] | undefined) ?? [];
    return rows.length ? `${g.label}: ${rows.length}` : "";
  })
    .filter(Boolean)
    .join("\n");
}

function GapRow({ label, hint, rows }: { label: string; hint: string; rows: string[] }) {
  const [open, setOpen] = useState(false);
  const complete = rows.length === 0;
  const trigger = (
    <button
      type="button"
      onClick={() => !complete && setOpen((v) => !v)}
      className="flex w-full items-start gap-2 text-left"
      disabled={complete}
    >
      {complete ? (
        <Check size={14} className="mt-0.5 shrink-0 text-[var(--color-success)]" />
      ) : (
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[var(--color-warning)]" />
      )}
      <span className="flex-1 text-sm">{label}</span>
      <Badge color={complete ? "green" : "yellow"}>{complete ? "komplet" : rows.length}</Badge>
    </button>
  );
  return (
    <div className="border-t border-[var(--color-border-muted)] py-2 first:border-t-0">
      {complete ? trigger : (
        <Tooltip content={shortList(rows)} className="block">
          {trigger}
        </Tooltip>
      )}
      {!complete && open && (
        <div className="mt-2 pl-6 space-y-1">
          <p className="text-xs text-[var(--color-text-muted)]">{hint}</p>
          <p className="font-mono text-xs">{rows.join(" · ")}</p>
        </div>
      )}
    </div>
  );
}

function GeneratedRow({ row, onEdit }: { row: GeneratedDoc; onEdit: (row: GeneratedDoc) => void }) {
  const report = (row.report ?? {}) as Partial<TomCompletenessReport>;
  const gaps = GAPS.reduce((sum, g) => sum + ((report[g.key] as string[] | undefined)?.length ?? 0), 0);
  return (
    <tr className="border-t border-[var(--color-border-muted)] align-top">
      {/* Poprawiony ręcznie tom to WCIĄŻ TEN SAM tom — wiersz nie rozmnaża się na każdy
          zapis, tylko pokazuje datę OSTATNIEJ edycji (data złożenia zostaje w tooltipie,
          żeby nic nie przepadło). */}
      <td className="px-3 py-2 whitespace-nowrap" title={
        row.edited_at ? `Złożono: ${fmtDateTime(row.generated_at)}` : undefined
      }>
        {row.edited_at ? (
          <span className="inline-flex flex-col">
            <span>{fmtDateTime(row.edited_at)}</span>
            <span className="text-[11px] text-[var(--color-text-muted)]">edytowano</span>
          </span>
        ) : (
          fmtDateTime(row.generated_at)
        )}
      </td>
      <td className="px-3 py-2">
        {(row.edited_at ? row.edited_by_name : row.generated_by_name) || "—"}
      </td>
      <td className="px-3 py-2">
        {row.status === "failure" ? (
          <span className="text-[var(--color-danger)]">{row.error || "Błąd generowania"}</span>
        ) : row.status === "pending" ? (
          <span className="text-[var(--color-text-muted)]">w trakcie…</span>
        ) : (
          <span className="flex flex-wrap items-center gap-3">
            {row.file?.url && (
              <a href={row.file.url} download className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
                <Download size={12} /> część redagowana (ODT)
              </a>
            )}
            {row.pdf_file?.url && (
              <a href={row.pdf_file.url} download className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
                <Download size={12} /> cały tom (PDF)
              </a>
            )}
            {row.file && (
              <button
                type="button"
                onClick={() => onEdit(row)}
                className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
              >
                <PencilLine size={12} /> edytuj
              </button>
            )}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {row.status === "success" && (gaps ? (
          <Tooltip content={gapBreakdown(report)}>
            <Badge color="yellow">{gaps} braków</Badge>
          </Tooltip>
        ) : (
          <Badge color="green">komplet</Badge>
        ))}
      </td>
    </tr>
  );
}

export function TomTitlesTab({ connection }: { connection: PowerProject }) {
  const overview = useTomTitles(connection.id);
  const history = useGeneratedHistory(TOM_TITLES_CONTEXT, connection.id);
  const generate = useGenerateTomTitles(connection.id);
  const [templateId, setTemplateId] = useState("");
  const [editing, setEditing] = useState<GeneratedDoc | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  const running = generate.isPending || (!!taskId && !["success", "failure", "cancelled"].includes(status ?? ""));

  // Odświeżamy kartę i historię DOKŁADNIE RAZ, gdy zadanie dobiegnie końca — bez
  // strażnika refetch → render → efekt → refetch kręciłby się w kółko.
  const doneRef = useRef<string | null>(null);
  useEffect(() => {
    if (taskId && (status === "success" || status === "failure") && doneRef.current !== taskId) {
      doneRef.current = taskId;
      overview.refetch();
      history.refetch();
    }
  }, [taskId, status, overview, history]);

  const data = overview.data;
  const report = data?.report;

  return (
    <div className="mt-4 space-y-4">
      <SectionCard
        title="TOM II — Tytuły prawne do nieruchomości"
        action={
          <div className="flex items-center gap-2">
            {(data?.templates.length ?? 0) > 1 && (
              <select
                className="input-base text-xs"
                value={templateId || data?.template?.id || ""}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={running}
              >
                {data?.templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>{t.name}</option>
                ))}
              </select>
            )}
            <Button
              size="sm"
              disabled={running || !!data?.blocked || !data}
              onClick={() =>
                generate
                  .mutateAsync(templateId ? { template_id: templateId } : undefined)
                  .then((r) => setTaskId(r.task_id))
                  .catch(() => undefined)
              }
            >
              {running ? <Spinner size="sm" /> : <FileText size={14} />}
              {running ? "Składam tom…" : "Generuj TOM II"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-xs text-[var(--color-text-muted)]">
            Składa cały tom z danych projektu: stronę tytułową, oświadczenia projektanta,
            wykaz właścicieli i tytułów prawnych oraz protokół z badania ksiąg wieczystych —
            a za przekładkami dokleja skany wypisów z rejestru gruntów i tytułów prawnych.
            Wynik to <b>edytowalny DOCX</b> części redagowanej (do poprawek redakcyjnych)
            i <b>scalony PDF całości</b>. Kolejność wiodąca: obręb → numer działki.
          </p>

          {overview.isLoading && <Spinner />}
          {overview.isError && (
            <p className="text-sm text-[var(--color-danger)]">{apiErr(overview.error)}</p>
          )}

          {data?.blocked && (
            <p className="flex items-start gap-2 border border-[var(--color-warning)] px-3 py-2 text-sm">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[var(--color-warning)]" />
              <span>{data.blocked}</span>
            </p>
          )}
          {generate.isError && (
            <p className="text-sm text-[var(--color-danger)]">{apiErr(generate.error)}</p>
          )}
          {taskId && status === "failure" && (
            <p className="text-sm text-[var(--color-danger)]">
              {task.data?.error || "Składanie tomu nie powiodło się — szczegóły w zadaniach w tle."}
            </p>
          )}

          {report && (
            <div>
              <p className="section-label mb-1">Raport kompletności</p>
              <p className="text-xs text-[var(--color-text-muted)] mb-1">
                Działki w tomie: <b>{report.parcels}</b> (uzgadniane: {report.consent_parcels}).
                Braki nie blokują generowania — tom powstanie z pustymi miejscami do
                uzupełnienia ręcznego.
              </p>
              {GAPS.map((g) => (
                <GapRow
                  key={g.key}
                  label={g.label}
                  hint={g.hint}
                  rows={(report[g.key] as string[] | undefined) ?? []}
                />
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Historia generacji">
        {history.isLoading ? (
          <Spinner />
        ) : (history.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Tom nie był jeszcze generowany. Każde uruchomienie zostawia tu osobny wpis
            z plikami — nic nie jest nadpisywane.
          </p>
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Kiedy</Th>
                <Th>Kto</Th>
                <Th>Pliki</Th>
                <Th className="text-right">Kompletność</Th>
              </tr>
            </thead>
            <tbody>
              {history.data?.map((row) => (
                <GeneratedRow key={row.id} row={row} onEdit={setEditing} />
              ))}
            </tbody>
          </TableShell>
        )}
      </SectionCard>

      {editing && (
        <OdtEditorScreen
          kind="generated"
          id={editing.id}
          title={`TOM II — Tytuły prawne · ${connection.project_name}`}
          onClose={() => {
            setEditing(null);
            // Zapis w edytorze = NOWA WERSJA tomu: nowy ODT i przebudowany od zera
            // scalony PDF (ze skanami), więc karta i historia muszą się przeliczyć.
            overview.refetch();
            history.refetch();
          }}
        />
      )}
    </div>
  );
}
